/**
 * The sales a receipt is drawn from when there is no real one.
 *
 * Here rather than in the console, and that is the whole reason this file
 * exists as a file. Three places draw a slip nobody bought: the designer's
 * preview, the kiosk's own test print, and the **proof** a console operator
 * sends to a real printer in a real shop. A proof drawn from a different sale
 * from the preview it was requested beside is not a proof of anything — it is
 * two pictures somebody has to hold in their head at once — and the moment the
 * kiosk needed the same sample the console had, a copy in each was a copy that
 * would drift.
 *
 * `packages/domain` is the only import, so this stays on the DOM-free half of
 * the package and the server can read the ids without pulling a renderer in.
 *
 * ── Why there is more than one ────────────────────────────────────────────
 *
 * The first is contrived on purpose: a discount, change, a company, an add-on,
 * a note and a lottery number at once, so that every switch in the designer
 * visibly does something. That is the right sample for **arranging** and the
 * wrong one for **checking** — nobody's receipt looks like it, and the question
 * an operator arrives with is what an ordinary card sale looks like.
 *
 * The last is the one nothing else in this system can show: a sale whose
 * e-Barimt filing has not come back, which prints «И-баримт хүлээгдэж байна»
 * where the lottery number goes. Every shop hands that receipt out on every
 * uplink wobble.
 *
 * **Ө and Ү are in the product names on purpose.** They are the reason raster
 * is the default mode, and every one of the three drawings above is a place
 * somebody should be able to see what text mode does to them.
 */
import { major, parse } from '@mgc/domain'
import type { SlipBranch, SlipOrder } from './slip-model'

/**
 * e-Barimt's three, at the size the real ones are.
 *
 * **The length is the whole point of writing them out here.** A sample is drawn
 * to prove a layout, and a fiscal block drawn from short mock values proves the
 * wrong layout. The ДДТД is **33 digits** — `docs/createtalon.md`, confirmed
 * with 1C on 2026-09-17 — and the fifteen-digit stand-in these used to carry
 * sat comfortably in a row the real number does not fit: measured at 32
 * columns, `row()` spends its whole budget on the value, truncates «ДДТД» to a
 * bare ellipsis and still returns 36 characters, which the head wraps mid-
 * number. The redesign prints it as a line of text, which wraps rather than
 * truncates — and the sample is what shows how it wraps on a narrow roll. The QR is the same argument in modules rather than characters:
 * `PosAPI_QRcode` comes back around 140 bytes, which is a version 7 code at
 * level M, where a 25-byte URL draws a version 2 that says nothing about
 * whether a head can resolve the real one.
 *
 * They are **deliberately not plausible as real receipts**. The QR scans to a
 * URL that says `sample=1`, because the one thing worse than a sample that
 * proves nothing is a sample somebody files a VAT claim against.
 */
const SAMPLE_QR =
  'https://ebarimt.mn/?sample=1&ddtd=100000000000000430003610044225' +
  '&amt=18700&dt=2026-09-09T10:15:00&sig=U0FNUExFLU5PVC1BLVJFQUwtUkVDRUlQVA'

/** The shop a sample is drawn in, where no real one is to hand. */
export const SAMPLE_BRANCH: SlipBranch = {
  name: 'MegaCoffee Төв',
  address: 'Улаанбаатар, Чингэлтэй дүүрэг',
  wifiName: 'MegaCoffee',
  wifiPassword: 'coffee2026'
}

export interface SampleSale {
  readonly id: string
  readonly label: string
  /** What this sale is for, in the words somebody would use to ask for it. */
  readonly detail: string
  readonly order: SlipOrder
}

/** Every conditional section has something to print. See the header. */
const EVERYTHING: SlipOrder = {
  displayNumber: 'A-14',
  orderType: 'eat_in',
  placedAt: '2026-09-09T10:15:00.000Z',
  gross: major(18700),
  net: parse('17000'),
  vat: parse('1700'),
  savings: major(500),
  change: major(1300),
  lines: [
    {
      name: 'Хүчил Өвөр латте',
      quantity: 2,
      total: major(13800),
      note: 'сахаргүй',
      addOns: [{ label: 'Нэмэлт шот', quantity: 1, total: major(1000) }]
    },
    { name: 'Бялуу', quantity: 1, total: major(4900), note: null, addOns: [] }
  ],
  payments: [{ method: 'cash', amount: major(20000), reference: null }],
  company: { registrationNumber: '1234567', name: 'Очирлан ХХК' },
  fiscal: { lottery: 'AB12345678', billId: '100000000000000430003610044225123', qrData: SAMPLE_QR }
}

/** The commonest receipt in the chain, and what the other three are built on. */
const CARD: SlipOrder = {
  displayNumber: 'A-15',
  orderType: 'take_out',
  placedAt: '2026-09-09T10:22:00.000Z',
  gross: major(6900),
  net: parse('6272.73'),
  vat: parse('627.27'),
  savings: major(0),
  change: major(0),
  lines: [{ name: 'Хүйтэн латте', quantity: 1, total: major(6900), note: null, addOns: [] }],
  payments: [{ method: 'card', amount: major(6900), reference: '004521' }],
  company: null,
  fiscal: { lottery: 'CD87654321', billId: '100000000000000430003710044225456', qrData: SAMPLE_QR }
}

export const SAMPLE_SALES: readonly SampleSale[] = [
  {
    id: 'everything',
    label: 'Everything at once',
    detail: 'A discount, change, a company and an add-on, so every section has something to print.',
    order: EVERYTHING
  },
  {
    id: 'card',
    label: 'One drink, card',
    detail: 'The commonest receipt in the chain. No discount, no change, no company.',
    order: CARD
  },
  {
    id: 'cash',
    label: 'Cash, with change',
    detail: 'Two lines paid with a note, so the change line prints.',
    order: {
      ...CARD,
      displayNumber: 'A-16',
      orderType: 'eat_in',
      gross: major(11800),
      net: parse('10727.27'),
      vat: parse('1072.73'),
      change: major(8200),
      lines: [
        { name: 'Америкaно', quantity: 1, total: major(4900), note: null, addOns: [] },
        { name: 'Ирмүүн бялуу', quantity: 1, total: major(6900), note: 'багцлах', addOns: [] }
      ],
      payments: [{ method: 'cash', amount: major(20000), reference: null }]
    }
  },
  {
    id: 'company',
    label: 'A company receipt',
    detail: 'Paid by QPay and registered to a company, which is the longest this slip gets.',
    order: {
      ...CARD,
      displayNumber: 'A-17',
      payments: [{ method: 'qpay', amount: major(6900), reference: 'QP-88213' }],
      company: { registrationNumber: '1234567', name: 'Очирлан ХХК' }
    }
  },
  {
    id: 'pending',
    label: 'e-Barimt not filed yet',
    detail: 'What a customer is handed when the uplink is down. No lottery number and no QR.',
    order: { ...CARD, displayNumber: 'A-18', fiscal: null }
  }
]

/**
 * The ids, as a tuple, so `z.enum` and this list cannot drift apart.
 *
 * Derived rather than declared beside the array, for the reason
 * `template.ts` derives `SectionKind` from `SECTION_KINDS`: an id in the schema
 * and not in the list is a sale the console can request and the kiosk cannot
 * draw — and the failure lands on a printer in a shop.
 */
export const SAMPLE_SALE_IDS = SAMPLE_SALES.map((sale) => sale.id) as [string, ...string[]]

/**
 * One sale by id, falling back to the first.
 *
 * A fallback rather than a throw, and it is the kiosk that decides this: an id
 * this page does not know came from a console newer than it, and printing the
 * wrong sample is a worse answer than printing none only if somebody is
 * comparing them side by side — where refusing to print at all is a printer the
 * operator now believes is broken.
 */
export const sampleSale = (id: string): SampleSale => SAMPLE_SALES.find((sale) => sale.id === id) ?? SAMPLE_SALES[0]!
